import { useToast } from '@chakra-ui/react';
import React, {
  useContext,
  useMemo,
  useState,
  createContext,
  useEffect,
} from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCreditFacilitiesQuery } from 'packages/credit-facility/requests/requests.gql.generated';

type CreditFacilityContextType = {
  creditFacilityId: string;
  // @bbplMonitor: add organizationId here for quick access
  setCreditFacilityId: (nextId: string) => void;
};
export const CreditFacilityContext = createContext<CreditFacilityContextType | null>(
  null,
);

export const useCreditFacilityContext = () => {
  const context = useContext(CreditFacilityContext);
  if (context === null) {
    throw new Error('CreditFacilityContext must be present');
  }

  return context;
};

type CreditFacilityProviderProps = {
  children: React.ReactNode;
  renderLoading: React.ReactElement;
  organizationId: string;
  fallbackComponent: React.ReactElement;
};
const CreditFacilityProvider = ({
  renderLoading,
  organizationId,
  children,
  fallbackComponent,
}: CreditFacilityProviderProps) => {
  const toast = useToast();
  const [creditFacilityId, setCreditFacilityId] = useState<string | null>(null);
  const { data, loading, refetch } = useGetCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    fetchPolicy: 'network-only',
    variables: { organizationId },
  });

  const context = useMemo(() => {
    const availableCreditFacilityIds = [];
    if (data && data.creditFacilities?.length > 0) {
      availableCreditFacilityIds.push(
        ...data.creditFacilities.map((cf) => cf.id),
      );
    }

    if (availableCreditFacilityIds.length === 0) {
      return null;
    }

    if (
      !creditFacilityId ||
      !availableCreditFacilityIds.includes(creditFacilityId)
    ) {
      return {
        creditFacilityId: availableCreditFacilityIds[0],
        setCreditFacilityId,
      };
    }

    return { creditFacilityId, setCreditFacilityId };
  }, [creditFacilityId, setCreditFacilityId, data]);

  useEffect(() => {
    refetch();
  }, [refetch, organizationId]);

  if (
    !loading &&
    data?.creditFacilities &&
    data.creditFacilities.length === 0
  ) {
    return fallbackComponent;
  }

  if (loading || !context) {
    return renderLoading;
  }

  return (
    <CreditFacilityContext.Provider value={context}>
      {children}
    </CreditFacilityContext.Provider>
  );
};

export default CreditFacilityProvider;
